const DASH_CHAR = '-';
export const ZERO_HOUR_MINUTE_SECOND = '00:00:00';
/**
 * Maximum number of days that report screens can find data to display on it
 */
export const DATA_RETENTION_DAYS_LIMIT = 90;
/**
 * Get the number of days between two dates
 *
 * @param startDateStrg - Start date on string format ('2020-01-01')
 * @param endDateStrg - End date on string format ('2020-01-01')
 *
 */
export const getDaysBetweenDates = (startDateStrg, endDateStrg) => {
    const startDate = new Date(startDateStrg);
    const endDate = new Date(endDateStrg);
    return Math.abs((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
};
/**
 * Get the number of days between a past date and today
 */
export const getDaysToToday = (startDate) => {
    return Math.abs((new Date().getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
};
/**
 * Transform time string to "1d00:00" or "00:00:00" format
 *
 * @param timeString - Time on string format ('dd.hh:mm:ss.mmm', 'dd.hh:mm:ss', 'hh:mm:ss', 'hh:mm:ss.mmm')
 * @param collapsed - Boolean indicating whether to collapse the seconds when the time to be displayed has days
 *
 */
export const formatTimeString = (timeString, collapsed = false) => {
    if (timeString !== undefined) {
        const timeStringArray = timeString.split('.');
        if (timeStringArray.length === 3) {
            return collapsed ?
                timeStringArray[0].concat('d ').concat(timeStringArray[1].slice(0, 5)) :
                timeStringArray[0].concat('d ').concat(timeStringArray[1]);
        }
        if (timeStringArray.length === 2) {
            if (timeStringArray[0].search(':') == -1) {
                return collapsed ?
                    timeStringArray[0].concat('d ').concat(timeStringArray[1].slice(0, 5)) :
                    timeStringArray[0].concat('d ').concat(timeStringArray[1]);
            }
            return timeStringArray[0];
        }
    }
    return timeString;
};
export const formatSecondsToDHMSMS = (totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0)
        return '00.00:00:00.000';
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.floor((totalSeconds - Math.floor(totalSeconds)) * 1000);
    const formattedDays = days.toString().padStart(2, '0') + '.';
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    const formattedMilliseconds = milliseconds.toString().padStart(3, '0');
    return `${days ? formattedDays : ''}${formattedHours}:${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};
export const formatEmptyTimeV1 = (value, zeroToDash, undefinedToDash) => {
    if (value === ZERO_HOUR_MINUTE_SECOND && zeroToDash) {
        return DASH_CHAR;
    }
    if (value === undefined && undefinedToDash) {
        return DASH_CHAR;
    }
    return value;
};
export const formatEmptyTimeV2 = (value) => {
    if (value === undefined) {
        return DASH_CHAR;
    }
    return value;
};
/**
 * Get DateTime from BR Format
 *
 * @param dateString should be on 'DD/MM/YYYY' format
 * @param time should have hours and minutes
 *
 */
export const getDatetimeFromBrFormat = (dateString, time) => {
    const [day, month, year] = dateString ? dateString.split('/') : [0, 0, 0];
    const dateTime = new Date(`${month}/${day}/${year} ${time.getHours()}:${time.getMinutes()}`);
    if (isNaN(dateTime.getTime())) {
        return new Date();
    }
    return dateTime;
};
/**
 * Get DateTime with applyed offset in timezone from BR Format
 *
 * @param dateString should be on 'DD/MM/YYYY' format
 * @param time should have hours and minutes
 * @param offSet should have hours diff to UTC
 *
 */
export const getDatetimeByOffsetFromBrFormat = (dateString, time, offSet) => {
    const [day, month, year] = dateString.split('/');
    const result = new Date(`${month}/${day}/${year} ${time.getHours()}:${time.getMinutes()}`);
    let timezoneOffset = time.getTimezoneOffset() * 60000;
    timezoneOffset += ((offSet ?? 0) * 60 * 60000);
    return new Date(result.getTime() - timezoneOffset);
};
/**
 * Get Date from BR Format
 *
 * @param dateString should be on 'DD/MM/YYYY' format
 *
 */
export const getDateFromBrFormat = (dateString) => {
    const [day, month, year] = dateString ? dateString.split('/') : [0, 0, 0];
    const date = new Date(`${month}/${day}/${year}`);
    if (isNaN(date.getTime())) {
        return new Date();
    }
    return date;
};
/**
 * Get Date from ISO format
 *
 * @param dateString should be on 'yyyy-mm-dd' format
 *
 */
export const getDateFromISOFormat = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return new Date(`${month}/${day}/${year}`);
};
export const getDateTimeFromISOFormat = (dateTimeString) => {
    const dateTime = new Date(dateTimeString);
    if (isNaN(dateTime.getTime())) {
        return new Date();
    }
    return dateTime;
};
/**
 * Return Date with time (or not). args prop should be numbers, and will be automatically added as you can see on Date constructor
 */
export const getDateTime = (...args) => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), ...args);
};
/**
 * This method makes the handling of a string that uses brackets
 * @param value - Term to be treated
 */
export const characterLeftSquareBracketTreatment = (value) => {
    return value.split('[').join('\[')
        .split(']').join('\]')
        .split('\\').join('\\\\');
};
/**
 * Returns a filtered array based in any of its properties value
 * @param term - Term to be filtered
 * @param arrayOfObject - Base array being filtered
 */
export const filterArray = (term, arrayOfObject) => {
    if (!term) {
        return arrayOfObject;
    }
    return arrayOfObject.filter((obj) => {
        return Object.keys(obj).reduce((acc, curr) => {
            if (!obj[curr]) {
                return acc;
            }
            return acc || obj[curr].toString().toLowerCase().includes(term.toLowerCase());
        }, false);
    });
};
